/**
 * Product Information PropTypes
 *
 * PropType definitions for detailed product data structures.
 *
 * @module propTypes/product-info
 */

import PropTypes from "prop-types";
import { reviewStatsShape } from "./review";

/**
 * Product information shape
 *
 * @property {string} name - Product name
 * @property {number} price - Base price
 * @property {number} price_with_tax - Price including tax
 * @property {string} description - Product description
 * @property {string} slug - URL slug
 * @property {Array} images - Product images
 */
export const productInfoShape = PropTypes.shape({
  name: PropTypes.string,
  price: PropTypes.number,
  price_with_tax: PropTypes.number,
  description: PropTypes.string,
  slug: PropTypes.string,
  images: PropTypes.array,
});

/**
 * Product actions props shape
 * Props for product action components (add to cart, wishlist, compare)
 *
 * @property {Object} product - Product object
 * @property {number} quantity - Selected quantity
 * @property {number} stock - Available stock
 * @property {number} cartQty - Quantity already in cart
 * @property {boolean} isAffiliate - Whether product has affiliate link
 * @property {string} affiliateLink - Affiliate link URL
 * @property {boolean} isInWishlist - Whether product is in wishlist
 * @property {boolean} isInCompare - Whether product is in compare list
 * @property {Function} onQuantityIncrease - Increase quantity handler
 * @property {Function} onQuantityDecrease - Decrease quantity handler
 * @property {Function} onAddToCart - Add to cart handler
 * @property {Function} onAddToWishlist - Add to wishlist handler
 * @property {Function} onAddToCompare - Add to compare handler
 */
export const productActionsPropsShape = {
  product: PropTypes.object.isRequired,
  quantity: PropTypes.number.isRequired,
  stock: PropTypes.number.isRequired,
  cartQty: PropTypes.number.isRequired,
  isAffiliate: PropTypes.bool,
  affiliateLink: PropTypes.string,
  isInWishlist: PropTypes.bool,
  isInCompare: PropTypes.bool,
  onQuantityIncrease: PropTypes.func.isRequired,
  onQuantityDecrease: PropTypes.func.isRequired,
  onAddToCart: PropTypes.func.isRequired,
  onAddToWishlist: PropTypes.func.isRequired,
  onAddToCompare: PropTypes.func.isRequired,
};

/**
 * Product with review stats shape
 * Product information combined with review statistics
 *
 * @property {Object} product - Product data
 * @property {Object} reviewStats - Review statistics
 */
export const productWithReviewsShape = PropTypes.shape({
  product: productInfoShape,
  reviewStats: reviewStatsShape,
});
